import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../../react-core/Html/CheckboxEditor";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { SectionHeader } from "../../SharedComponents";
import {
    maximumIncomePerPayment,
    TransferOrDrawdown,
    nbSippIncomePaymentDetailsLabels,
    nbSippIncomePaymentDetailsMessages,
} from "../../../Resource/Contents";

import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import {
    calculateMaximumIncomePaymentForFlexiTarget,
    maximumIncomePerPaymentForFlexiTransfers,
} from "../../../../Common/SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { processTypeEnum } from "../../../../Common/Resource/Contents";
import { PensionBase } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";

export type FlexiTransferRowData = {
    FlexiTransfer1: TransferOrDrawdown;
    FlexiTransfer2?: TransferOrDrawdown;
};

interface IFlexiTransferRowProps extends IProcessComponentProps {
    data: FlexiTransferRowData;
    onChange: (changes: any, transfer: TransferOrDrawdown) => void;
    pensionBase: PensionBase;
    isITFC: boolean;
}
interface IFlexiTransferRowState {
    takeFullValue1: boolean;
    takeFullValue2: boolean;
}

export class FlexiTransferRow extends ProcessComponent<IFlexiTransferRowProps, IFlexiTransferRowState> {
    constructor(props: any) {
        super(props);
        this.state = { takeFullValue1: false, takeFullValue2: false };
    }

    public render(): JSX.Element {
        const { data, onChange, pensionBase, isITFC } = this.props;
        const calculateFullValue = (transfer: TransferOrDrawdown) => {
            return calculateMaximumIncomePaymentForFlexiTarget(
                processTypeEnum.NewBusiness,
                transfer,
                pensionBase,
                isITFC)
        };

        const fullValue1 = calculateFullValue(data?.FlexiTransfer1);

        const fullValue2 = calculateFullValue(data?.FlexiTransfer2);

        return (
            <>
                {SectionHeader(nbSippIncomePaymentDetailsLabels.flexi)}
                <div className="row">
                    <span className="col-4 h4">{nbSippIncomePaymentDetailsLabels.from}</span>
                    <span className="col-4" data-test="from1">
                        {data.FlexiTransfer1.ProviderDetails?.Name}
                    </span>
                    {data.FlexiTransfer2 && (
                        <span className="col-4" data-test="from2">
                            {data.FlexiTransfer2.ProviderDetails?.Name}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{nbSippIncomePaymentDetailsLabels.amount}</span>
                    <span className="col-4" data-test="amount1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            maximumIncomePerPaymentForFlexiTransfers([data.FlexiTransfer1], true),
                            data.FlexiTransfer1.CurrentValue?.Currency || CurrencyCode.GBP
                        )}
                    </span>
                    {data.FlexiTransfer2 && (
                        <span className="col-4" data-test="amount2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                maximumIncomePerPaymentForFlexiTransfers([data.FlexiTransfer2], true),
                                data.FlexiTransfer2.CurrentValue?.Currency || CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{nbSippIncomePaymentDetailsLabels.type}</span>
                    <span className="col-4" data-test="type1">
                        {data.FlexiTransfer1.IsInheritedFund
                            ? nbSippIncomePaymentDetailsLabels.inherited
                            : nbSippIncomePaymentDetailsLabels.member}
                    </span>
                    {data.FlexiTransfer2 && (
                        <span className="col-4" data-test="type2">
                            {data.FlexiTransfer2.IsInheritedFund
                                ? nbSippIncomePaymentDetailsLabels.inherited
                                : nbSippIncomePaymentDetailsLabels.member}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{nbSippIncomePaymentDetailsLabels.MaximumIncomePerPayment}</span>
                    <span className="col-4" data-test="maximumIncomePerPayment1">
                        {MoneyDisplayHelper.FromatMoneyDisplay(
                            fullValue1,
                            CurrencyCode.GBP
                        )}
                    </span>
                    {data.FlexiTransfer2 && (
                        <span className="col-4" data-test="maximumIncomePerPayment2">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                fullValue2,
                                CurrencyCode.GBP
                            )}
                        </span>
                    )}
                </div>
                <div className="row">
                    <span className="col-4 h4">{nbSippIncomePaymentDetailsLabels.incomePerPayment}</span>
                    <div className="col-2">
                        <MoneyTextBoxEditor
                            data-test="incomePerPayment1"
                            label=""
                            value={data.FlexiTransfer1.IncomePaymentDetails.GrossIncomePerPayment?.Amount}
                            currency={
                                data.FlexiTransfer1.IncomePaymentDetails.GrossIncomePerPayment?.Currency ||
                                CurrencyCode.GBP
                            }
                            onChange={(v) =>
                                onChange(
                                    {
                                        GrossIncomePerPayment: {
                                            Amount: v,
                                            Currency:
                                                data.FlexiTransfer1?.IncomePaymentDetails?.GrossIncomePerPayment
                                                    ?.Currency || CurrencyCode.GBP,
                                        },
                                    },
                                    data.FlexiTransfer1
                                )
                            }
                            atMost={{
                                errorMessage: nbSippIncomePaymentDetailsMessages.cannotBeGreaterThanMaximumPerIncomePayment,
                                value: fullValue1,
                            }}
                            disabled={this.state.takeFullValue1}
                        />
                    </div>
                    <CheckboxEditor
                        data-test="takeFullValue1"
                        label={nbSippIncomePaymentDetailsLabels.takeFullValue}
                        value={this.state.takeFullValue1}
                        onChange={(v) => {
                            v
                                ? onChange(
                                      {
                                          GrossIncomePerPayment: {
                                              Amount: fullValue1,
                                              Currency:
                                                  data?.FlexiTransfer1?.IncomePaymentDetails?.GrossIncomePerPayment
                                                      ?.Currency || CurrencyCode.GBP,
                                          },
                                      },
                                      data.FlexiTransfer1
                                  )
                                : onChange(
                                      {
                                          GrossIncomePerPayment: {
                                              Amount: null,
                                              Currency:
                                                  data?.FlexiTransfer1?.IncomePaymentDetails?.GrossIncomePerPayment
                                                      ?.Currency || CurrencyCode.GBP,
                                          },
                                      },
                                      data.FlexiTransfer1
                                  );
                            this.setState({ takeFullValue1: v });
                        }}
                        cssWrapper="col-2 mt-12 hyperlink"
                    />
                    {data.FlexiTransfer2 && (
                        <>
                            <div className="col-2">
                                <MoneyTextBoxEditor
                                    data-test="incomePerPayment2"
                                    label=""
                                    value={data.FlexiTransfer2.IncomePaymentDetails.GrossIncomePerPayment?.Amount}
                                    currency={
                                        data.FlexiTransfer2.IncomePaymentDetails.GrossIncomePerPayment?.Currency ||
                                        CurrencyCode.GBP
                                    }
                                    onChange={(v) =>
                                        onChange(
                                            {
                                                GrossIncomePerPayment: {
                                                    Amount: v,
                                                    Currency:
                                                        data.FlexiTransfer2?.IncomePaymentDetails?.GrossIncomePerPayment
                                                            ?.Currency || CurrencyCode.GBP,
                                                },
                                            },
                                            data.FlexiTransfer2
                                        )
                                    }
                                    atMost={{
                                        errorMessage: nbSippIncomePaymentDetailsMessages.cannotBeGreaterThanMaximumPerIncomePayment,
                                        value: fullValue2,
                                    }}
                                    disabled={this.state.takeFullValue2}
                                />
                            </div>
                            <CheckboxEditor
                                data-test="takeFullValue2"
                                label={nbSippIncomePaymentDetailsLabels.takeFullValue}
                                value={this.state.takeFullValue2}
                                onChange={(v) => {
                                    v
                                        ? onChange(
                                              {
                                                  GrossIncomePerPayment: {
                                                      Amount: fullValue2,
                                                      Currency:
                                                          data?.FlexiTransfer2?.IncomePaymentDetails
                                                              ?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP,
                                                  },
                                              },
                                              data.FlexiTransfer2
                                          )
                                        : onChange(
                                              {
                                                  GrossIncomePerPayment: {
                                                      Amount: null,
                                                      Currency:
                                                          data?.FlexiTransfer2?.IncomePaymentDetails
                                                              ?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP,
                                                  },
                                              },
                                              data.FlexiTransfer2
                                          );
                                    this.setState({ takeFullValue2: v });
                                }}
                                cssWrapper="col-2 mt-12 hyperlink"
                            />
                        </>
                    )}
                </div>
                <hr />
            </>
        );
    }
}

export default FlexiTransferRow;
